import { squelchMicForMs } from './micSquelch'
import { isAnyoneSpeakingInCall } from './speechGate'
import { BACKEND_URL } from './config'

// OSPROD-74: every UI sound goes through `squelch()` first so the
// published microphone track is briefly disabled for the duration of
// the playback. Without this, melodic sounds (ringtone, the channel-
// join chord, the knock) leaked via the speaker→mic acoustic path and
// other call participants heard our local UI as if it were our voice.
// AEC + RNNoise stripped most of it but the residual was bad enough
// for QA to flag. The tail-buffer accounts for envelope fade-outs and
// the 1-frame Web Audio scheduling jitter so the squelch lasts
// slightly longer than the audible sound.
const SQUELCH_TAIL_MS = 60
// Grace window for the reconnect-aware self join/leave tones (see selfLeave).
// A reconnect (deploy / brief net drop) recovers well within this; a genuine
// disconnect does not. 5s matches the user-requested debounce.
const RECONNECT_GRACE_MS = 5000

// A resolved system-sound override for one event.
interface SoundChoice {
  mode: string // 'builtin' | 'file' | 'off'
  fileUrl: string | null
  volume: number
}

class SoundService {
  private ctx: AudioContext | null = null
  // Tracks whether we've registered the global gesture-unlock handler.
  // Some Chromium revs ignore the `autoplay-policy=no-user-gesture-required`
  // command-line switch in Electron 30 and keep the AudioContext suspended
  // until the user clicks ANYTHING — without this hook the very first
  // selfJoin/selfLeave/otherJoin call after page load fires into a
  // suspended ctx and is silent. Re-arm on every gesture is cheap (resume
  // is idempotent) and self-removes after the first run-through.
  private gestureHooked = false

  // Resolved system-sound config: eventKey → choice. Empty until
  // loadConfig() runs; an absent key resolves to the built-in default,
  // i.e. exactly the legacy behaviour. Refreshed on `system-sounds:updated`.
  private config: Record<string, SoundChoice> = {}
  // Decoded AudioBuffer cache for file-mode clips, keyed by resolved URL.
  private clipCache = new Map<string, AudioBuffer>()

  // Mute the published mic for the audible duration of a UI sound + a
  // small tail to cover the gain envelope's exponential fade-out.
  private squelch(durationSec: number) {
    squelchMicForMs(Math.round(durationSec * 1000) + SQUELCH_TAIL_MS)
  }

  private hookGesture() {
    if (this.gestureHooked || typeof window === 'undefined') return
    this.gestureHooked = true
    const unlock = () => {
      if (this.ctx && this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {})
      }
    }
    window.addEventListener('pointerdown', unlock, { capture: true, passive: true })
    window.addEventListener('keydown', unlock, { capture: true, passive: true })
  }

  private getCtx(): AudioContext {
    this.hookGesture()
    if (!this.ctx) {
      this.ctx = new AudioContext()
    }
    if (this.ctx.state === 'suspended') {
      // Fire-and-forget resume. The gesture hook above keeps trying on
      // every user interaction, so the next sound after a click plays
      // even if this resume() loses the race.
      this.ctx.resume().catch(() => {})
    }
    return this.ctx
  }

  private beep(frequency: number, duration: number, type: OscillatorType = 'sine', volume = 0.15) {
    this.squelch(duration)
    const ctx = this.getCtx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = type
    osc.frequency.setValueAtTime(frequency, ctx.currentTime)

    gain.gain.setValueAtTime(volume, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration)

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + duration)
  }

  private chord(freqs: number[], duration: number, volume = 0.12) {
    // The last note starts at (freqs.length - 1) * 0.02s after the
    // first, so the audible tail extends past `duration` by that gap.
    this.squelch(duration + Math.max(0, freqs.length - 1) * 0.02)
    const ctx = this.getCtx()
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.02)

      gain.gain.setValueAtTime(0, ctx.currentTime)
      gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.03)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration)

      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.start(ctx.currentTime + i * 0.02)
      osc.stop(ctx.currentTime + duration)
    })
  }

  // ─────────────────────────────────────────────────────────────
  //  System-sound config resolution (admin-managed overrides)
  // ─────────────────────────────────────────────────────────────

  async loadConfig(): Promise<void> {
    try {
      // Lazy import to avoid a static import cycle (api → authStore → sounds).
      const { systemSoundsApi } = await import('./api')
      const { data } = await systemSoundsApi.list()
      const next: Record<string, SoundChoice> = {}
      for (const e of data.events) next[e.eventKey] = { mode: e.mode, fileUrl: e.fileUrl, volume: e.volume }
      this.config = next
    } catch (err) {
      console.warn('[sounds] loadConfig failed; using built-in defaults', err)
    }
  }

  // Dispatch: off → silent; file → decoded clip (with mic squelch); else
  // the built-in synth registered for this eventKey.
  private playEvent(eventKey: string) {
    const cfg = this.config[eventKey]
    if (cfg?.mode === 'off') return
    if (cfg?.mode === 'file' && cfg.fileUrl) { void this.playClip(cfg.fileUrl, cfg.volume ?? 1); return }
    this.builtins[eventKey]?.()
  }

  // Admin preview of an arbitrary (possibly unsaved) choice for an event.
  preview(choice: { eventKey: string; mode: string; fileUrl?: string | null; volume?: number }) {
    if (choice.mode === 'off') return
    if (choice.mode === 'file' && choice.fileUrl) { void this.playClip(choice.fileUrl, choice.volume ?? 1); return }
    this.builtins[choice.eventKey]?.()
  }

  private resolveUrl(fileUrl: string): string {
    if (/^https?:/i.test(fileUrl)) return fileUrl
    return `${BACKEND_URL}${fileUrl}`
  }

  // Decode (cached) + play a clip on the service's own AudioContext, with
  // the mic squelched for the clip duration so it can't leak into a live
  // call as the user's voice (OSPROD-74).
  private async playClip(fileUrl: string, volume = 1): Promise<void> {
    try {
      const url = this.resolveUrl(fileUrl)
      const ctx = this.getCtx()
      let buffer = this.clipCache.get(url)
      if (!buffer) {
        const res = await fetch(url, { credentials: 'include' })
        if (!res.ok) { console.warn('[sounds] clip fetch failed', { url, status: res.status }); return }
        buffer = await ctx.decodeAudioData(await res.arrayBuffer())
        this.clipCache.set(url, buffer)
      }
      this.squelch(buffer.duration)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      const gain = ctx.createGain()
      gain.gain.value = Math.max(0, Math.min(1, volume))
      source.connect(gain)
      gain.connect(ctx.destination)
      source.start(0)
      source.onended = () => {
        try { source.disconnect() } catch { /* noop */ }
        try { gain.disconnect() } catch { /* noop */ }
      }
    } catch (err) {
      console.warn('[sounds] clip play failed', { fileUrl, err })
    }
  }

  // Built-in synth implementations, keyed by eventKey. Each body is the
  // original (calibrated) oscillator routine — do not change frequencies,
  // durations, gains or envelopes (see services/CLAUDE.md). playEvent()
  // and preview() invoke these when no file override is configured.
  private builtins: Record<string, () => void> = {
    'mic.mute': () => { this.beep(400, 0.12, 'sine', 0.12) },
    'mic.unmute': () => { this.beep(600, 0.12, 'sine', 0.12) },
    'audio.deafen': () => { this.beep(300, 0.18, 'triangle', 0.1) },
    'audio.undeafen': () => { this.beep(500, 0.15, 'sine', 0.12) },
    'message.new': () => { this.beep(800, 0.06, 'sine', 0.06) },
    'message.notification': () => {
      this.squelch(0.18) // 0.1s + 80ms delay + 0.1s tone fade-out
      this.beep(523, 0.1, 'sine', 0.08)
      setTimeout(() => this.beep(659, 0.1, 'sine', 0.08), 80)
    },
    // OSPROD-79: short, gentle two-tone chirp for incoming reactions on the
    // current user's messages. Quieter than message()/notification() because
    // reactions arrive much more often per conversation.
    'message.reaction': () => {
      this.beep(880, 0.05, 'sine', 0.05)
      setTimeout(() => this.beep(1175, 0.05, 'sine', 0.05), 50)
    },
    'voice.selfJoin': () => {
      // Clear ascending tone for self joining
      this.squelch(0.3) // 3 notes spread by 50ms, each 200ms long
      const ctx = this.getCtx()
      const freqs = [523, 659, 784]
      freqs.forEach((freq, i) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.05)
        gain.gain.setValueAtTime(0, ctx.currentTime)
        gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + i * 0.05 + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.05 + 0.2)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(ctx.currentTime + i * 0.05)
        osc.stop(ctx.currentTime + i * 0.05 + 0.2)
      })
    },
    'voice.selfLeave': () => {
      // Descending tone for self leaving
      this.squelch(0.26) // 3 notes spread by 40ms, each 180ms long
      const ctx = this.getCtx()
      const freqs = [784, 659, 523]
      freqs.forEach((freq, i) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.04)
        gain.gain.setValueAtTime(0, ctx.currentTime)
        gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + i * 0.04 + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.04 + 0.18)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(ctx.currentTime + i * 0.04)
        osc.stop(ctx.currentTime + i * 0.04 + 0.18)
      })
    },
    'voice.otherJoin': () => {
      // Subtle tone for others joining
      this.beep(440, 0.1, 'sine', 0.06)
    },
    'voice.otherLeave': () => {
      // Subtle tone for others leaving
      this.beep(400, 0.1, 'sine', 0.05)
    },
    'voice.knock': () => {
      // ICQ-style knock-knock-knock when someone joins your voice room
      this.squelch(0.28) // 3 knocks at 0/100/200ms, each 80ms long
      const ctx = this.getCtx()
      const delays = [0, 0.1, 0.2]
      delays.forEach((t) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(720, ctx.currentTime + t)
        gain.gain.setValueAtTime(0, ctx.currentTime + t)
        gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + t + 0.01)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.08)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(ctx.currentTime + t)
        osc.stop(ctx.currentTime + t + 0.08)
      })
    },
    'call.connected': () => {
      // Pleasant ascending double tone: "connected"
      this.squelch(0.3) // 0.12s + 120ms delay + 0.18s tone tail
      this.beep(880, 0.12, 'sine', 0.1)
      setTimeout(() => this.beep(1100, 0.18, 'sine', 0.08), 120)
    },
    'call.endedRemote': () => {
      // Two descending tones: "remote hung up"
      this.squelch(0.57) // 0.2s + 220ms delay + 0.35s tone tail
      this.beep(440, 0.2, 'sine', 0.1)
      setTimeout(() => this.beep(330, 0.35, 'sine', 0.08), 220)
    },
    'call.endedSelf': () => {
      // Single short confirm tone: "you hung up"
      this.beep(660, 0.12, 'sine', 0.08)
    },
    'screenshare.start': () => {
      // Ascending two-tone: "sharing on"
      this.beep(660, 0.1, 'sine', 0.1)
      setTimeout(() => this.beep(990, 0.12, 'sine', 0.09), 90)
    },
    'screenshare.stop': () => {
      // Descending two-tone: "sharing off"
      this.beep(990, 0.1, 'sine', 0.09)
      setTimeout(() => this.beep(660, 0.12, 'sine', 0.08), 90)
    },
  }

  // ─────────────────────────────────────────────────────────────
  //  Public API — thin dispatchers (names preserved for all callers,
  //  incl. os-messenger which imports this same singleton).
  // ─────────────────────────────────────────────────────────────

  mute() { this.playEvent('mic.mute') }
  unmute() { this.playEvent('mic.unmute') }
  deafen() { this.playEvent('audio.deafen') }
  undeafen() { this.playEvent('audio.undeafen') }

  // NOT in the admin catalog — kept as direct built-ins.
  channelJoin() {
    // Discord-like ascending connect sound
    this.chord([440, 554, 659], 0.35, 0.1)
  }
  channelLeave() {
    // Discord-like descending disconnect sound
    this.chord([659, 554, 440], 0.3, 0.08)
  }
  connect() {
    this.beep(880, 0.08, 'sine', 0.08)
  }
  disconnect() {
    this.beep(330, 0.15, 'sine', 0.08)
  }

  // "Всё внимание на спикера" (2026-06-12 big-call feedback): presence and
  // message pings hold their tongue while anyone on the live call is
  // talking. In a 27-person call with connection churn these fired
  // non-stop over the speaker. Ringtones and self join/leave cues are
  // deliberately NOT gated — see speechGate.ts.
  message() {
    if (isAnyoneSpeakingInCall()) return
    this.playEvent('message.new')
  }
  notification() {
    if (isAnyoneSpeakingInCall()) return
    this.playEvent('message.notification')
  }
  reaction() {
    if (isAnyoneSpeakingInCall()) return
    this.playEvent('message.reaction')
  }

  // Reconnect-aware self join/leave. A deploy (server restart) or a brief
  // network drop produces a self-LEAVE immediately followed by a self-JOIN.
  // Playing the descending "left" tone then the ascending "joined" tone on
  // every blip is jarring — a top launch-day complaint (2026-06-09). So we
  // defer the leave tone by RECONNECT_GRACE_MS; if a join lands within that
  // window it was a reconnect, so we cancel the pending leave AND swallow the
  // join (silent recovery). A genuine leave with no rejoin still tones, just
  // late — the user already sees they left. A reconnect slower than the grace
  // window tones both, i.e. it's treated as a real disconnect (per spec).
  private selfLeaveTimer: ReturnType<typeof setTimeout> | null = null

  selfJoin() {
    if (this.selfLeaveTimer) {
      // Left < grace ago and we're back: a reconnect. Stay silent.
      clearTimeout(this.selfLeaveTimer)
      this.selfLeaveTimer = null
      return
    }
    this.playEvent('voice.selfJoin')
  }
  // `immediate` = the user deliberately clicked Disconnect — there is no
  // reconnect to wait out, so the tone plays right away instead of after
  // the grace window. Involuntary leaves (socket drop, takeover, errors)
  // keep the deferred path so network blips stay silent.
  selfLeave(immediate = false) {
    if (this.selfLeaveTimer) clearTimeout(this.selfLeaveTimer)
    if (immediate) {
      this.selfLeaveTimer = null
      this.playEvent('voice.selfLeave')
      return
    }
    this.selfLeaveTimer = setTimeout(() => {
      this.selfLeaveTimer = null
      this.playEvent('voice.selfLeave')
    }, RECONNECT_GRACE_MS)
  }
  otherJoin() {
    if (isAnyoneSpeakingInCall()) return
    this.playEvent('voice.otherJoin')
  }
  otherLeave() {
    if (isAnyoneSpeakingInCall()) return
    this.playEvent('voice.otherLeave')
  }
  knock() {
    if (isAnyoneSpeakingInCall()) return
    this.playEvent('voice.knock')
  }

  private ringtoneInterval: ReturnType<typeof setInterval> | null = null
  private ringtoneNodes: { osc: OscillatorNode; gain: GainNode }[] = []

  ringtone() {
    if (this.config['call.ringtone']?.mode === 'off') return
    if (this.ringtoneInterval) return
    const playRing = () => {
      const cfg = this.config['call.ringtone']
      if (cfg?.mode === 'file' && cfg.fileUrl) { void this.playClip(cfg.fileUrl, cfg.volume ?? 1); return }
      this.squelch(0.5) // each ring envelope decays over 500ms
      const ctx = this.getCtx()
      const freqs = [440, 554]
      this.ringtoneNodes.forEach((n) => {
        n.osc.stop()
      })
      this.ringtoneNodes = freqs.map((freq) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(freq, ctx.currentTime)
        gain.gain.setValueAtTime(0, ctx.currentTime)
        gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.5)
        return { osc, gain }
      })
    }
    playRing()
    this.ringtoneInterval = setInterval(playRing, 1500)
  }

  stopRingtone() {
    if (this.ringtoneInterval) {
      clearInterval(this.ringtoneInterval)
      this.ringtoneInterval = null
    }
    this.ringtoneNodes.forEach((n) => {
      try { n.osc.stop() } catch {}
    })
    this.ringtoneNodes = []
  }

  // ─────────────────────────────────────────────────────────────
  //  DM Call sounds
  // ─────────────────────────────────────────────────────────────

  private outgoingInterval: ReturnType<typeof setInterval> | null = null

  outgoingRing() {
    if (this.config['call.outgoingRing']?.mode === 'off') return
    if (this.outgoingInterval) return
    const playTone = () => {
      const cfg = this.config['call.outgoingRing']
      if (cfg?.mode === 'file' && cfg.fileUrl) { void this.playClip(cfg.fileUrl, cfg.volume ?? 1); return }
      // Telephone-like outgoing ring: 440Hz, 0.5s on, 1.5s off
      this.beep(440, 0.5, 'sine', 0.12)
    }
    playTone()
    this.outgoingInterval = setInterval(playTone, 2000)
  }

  stopOutgoingRing() {
    if (this.outgoingInterval) {
      clearInterval(this.outgoingInterval)
      this.outgoingInterval = null
    }
  }

  callConnected() { this.playEvent('call.connected') }
  callEndedByRemote() { this.playEvent('call.endedRemote') }
  callEndedBySelf() { this.playEvent('call.endedSelf') }

  // New: local screen-share start/stop (wired via mediaManager hooks).
  screenShareStart() { this.playEvent('screenshare.start') }
  screenShareStop() { this.playEvent('screenshare.stop') }
}

export const sounds = new SoundService()
